import {
  Account,
  Club,
  TournamentCompetition,
  TournamentCompetitionEvent,
  TournamentEntry,
  TournamentPlayer,
  TournamentRegistration,
  TournamentSession,
} from "@mixitone/models";
import { Button, Input, LabelledField, Select, Toggle } from "@mixitone/components";
import { ApplicationView } from "@mixitone/mvc";
import { List } from "@mixitone/oom";
import { SubscriptionManager } from "@mixitone/oom/SubscriptionManager";
import MixitoneController from "controllers/MixitoneController";
import RouteController from "controllers/RouteController";
import routeService from "services/RouteService";
import { withBatch } from "@mixitone/util";
import AppModal from "components/common/AppModal";
import { injectModal } from "lib/injectParams";
import { useState } from "react";
import {
  tournamentCompetitionCopier,
  tournamentEntryCopier,
  tournamentEventCopier,
  tournamentPlayerCopier,
  tournamentRegistrationCopier,
  tournamentSessionCopier,
} from "./tournamentCopyCopiers";

interface State {
  name: string;
  club_id: string;
  excludePersonalInfo: boolean;
  copyTournamentProgress: boolean;
  loading: boolean;
  clubs: List<Club>;
  sourceSession: TournamentSession | null;
  sourcePlayers: List<TournamentPlayer>;
  sourceCompetitions: List<TournamentCompetition>;
  sourceEntries: List<TournamentEntry>;
  sourceRegistrations: List<TournamentRegistration>;
  sourceEvents: List<TournamentCompetitionEvent>;
}

interface Props {
  id: string;
}

class CopyTournamentModalController extends MixitoneController<State, Props> {
  get initialState(): State {
    return {
      name: "",
      club_id: "",
      excludePersonalInfo: true,
      copyTournamentProgress: false,
      loading: false,
      clubs: new List(),
      sourceSession: null,
      sourcePlayers: new List(),
      sourceCompetitions: new List(),
      sourceEntries: new List(),
      sourceRegistrations: new List(),
      sourceEvents: new List(),
    };
  }

  override async initialize(props: Props) {
    await this.loadTournamentData(props.id);
  }

  @withBatch
  async loadTournamentData(id: string) {
    this.state.loading = true;
    try {
      // Load clubs for the account and filter by manage permission
      const allClubs = await Club.query().eq("account_id", Account.current!.id).orderBy("name").all();
      const clubsWithPermission = allClubs.filter((club) => club.hasManagePermission());
      this.state.clubs = new List(...clubsWithPermission);

      // Load tournament session
      const sessionQuery = TournamentSession.query().eq("account_id", Account.current!.id);
      const sourceSession = await sessionQuery.find(id);
      this.state.sourceSession = sourceSession;
      this.state.name = `${sourceSession.name} (Copy)`;
      // Only set club_id if the user has manage permission for that club, otherwise use first available
      const sourceClub = allClubs.find((club) => club.id === sourceSession.club_id);
      if (sourceClub?.hasManagePermission()) {
        this.state.club_id = sourceSession.club_id || "";
      } else if (clubsWithPermission.length > 0) {
        this.state.club_id = clubsWithPermission[0].id || "";
      }

      // Load competitions
      const competitionQuery = TournamentCompetition.query().eq("tournament_session_id", id);
      const competitions = await competitionQuery.orderBy("created_at", "desc").all();
      this.state.sourceCompetitions = new List(...competitions);

      // Load players
      const playersQuery = TournamentPlayer.query().eq("tournament_session_id", id);
      const players = await playersQuery.orderBy("name", "desc").all();
      this.state.sourcePlayers = new List(...players);

      // Load entries and registrations
      const competitionIds = competitions.map((c) => c.id!);
      const entries = await TournamentEntry.query()
        .in("tournament_competition_id", competitionIds)
        .orderBy("created_at", "desc")
        .all();
      this.state.sourceEntries = new List(...entries);

      const registrations = await TournamentRegistration.query()
        .in("tournament_competition_id", competitionIds)
        .all();
      this.state.sourceRegistrations = new List(...registrations);

      const events = await TournamentCompetitionEvent.query()
        .in("tournament_competition_id", competitionIds)
        .orderBy("created_at", "asc")
        .all();
      this.state.sourceEvents = new List(...events);
    } finally {
      this.state.loading = false;
    }
  }

  @withBatch
  async actionCopyTournament() {
    const sourceSession = this.state.sourceSession;
    if (!sourceSession?.id) {
      throw new Error("Cannot copy tournament: source tournament not loaded");
    }

    // Create a mapping of old IDs to new IDs for players, competitions, entries, and matches
    const playerIdMap = new Map<string, string>();
    const competitionIdMap = new Map<string, string>();
    const entryIdMap = new Map<string, string>();
    let newSessionId: string | undefined;

    await SubscriptionManager.startBatch(async () => {
      // 1. Copy tournament session
      const newSession = tournamentSessionCopier.copy(sourceSession, {
        status: this.state.copyTournamentProgress ? sourceSession.status : "setup",
        name: this.state.name || `${sourceSession.name} (Copy)`,
        club_id: this.state.club_id,
      });
      newSession.thank_you_sent_at = undefined;
      await newSession.save();
      newSessionId = newSession.id!;

      // 2. Copy all players
      for (const sourcePlayer of this.state.sourcePlayers) {
        const overrides: Record<string, any> = {
          tournament_session_id: newSessionId,
        };

        // Exclude personal information if requested
        if (this.state.excludePersonalInfo) {
          overrides.email = null;
          overrides.mobile = null;
          overrides.player_profile_id = null;
        }

        const newPlayer = tournamentPlayerCopier.copy(sourcePlayer, overrides);
        // Explicitly clear excluded fields after creation to ensure they're saved
        newPlayer.paid_at = undefined;
        newPlayer.confirmed_at = undefined;
        newPlayer.confirmation_sent_at = undefined;
        newPlayer.thank_you_sent_at = undefined;
        // Preserve checked_in_at and withdrawn_at if copying tournament progress
        if (!this.state.copyTournamentProgress) {
          newPlayer.checked_in_at = undefined;
          newPlayer.withdrawn_at = undefined;
        } else {
          newPlayer.checked_in_at = sourcePlayer.checked_in_at;
          newPlayer.withdrawn_at = sourcePlayer.withdrawn_at;
        }
        if (this.state.excludePersonalInfo) {
          newPlayer.email = undefined;
          newPlayer.mobile = undefined;
        }
        await newPlayer.save();
        playerIdMap.set(sourcePlayer.id!, newPlayer.id!);
      }

      // Helper function to recursively replace entry IDs in format_data
      const replaceEntryIdsInFormatData = (data: any, entryIdMap: Map<string, string>): any => {
        if (data === null || data === undefined) {
          return data;
        }

        // If it's a string and matches an entry ID, replace it
        if (typeof data === "string" && entryIdMap.has(data)) {
          return entryIdMap.get(data);
        }

        // If it's an array, recursively process each element
        if (Array.isArray(data)) {
          return data.map((item) => replaceEntryIdsInFormatData(item, entryIdMap));
        }

        // If it's an object, recursively process each value and key
        if (typeof data === "object") {
          const result: Record<string, any> = {};
          for (const [key, value] of Object.entries(data)) {
            // Replace key if it's an entry ID
            const newKey = typeof key === "string" && entryIdMap.has(key) ? entryIdMap.get(key)! : key;
            // Recursively process value
            result[newKey] = replaceEntryIdsInFormatData(value, entryIdMap);
          }
          return result;
        }

        // For primitives (number, boolean, etc.), return as-is
        return data;
      };

      // Store new competitions and entries for potential format_data update after all entries are copied
      const newCompetitions: Array<{ competition: TournamentCompetition; sourceFormatData: any }> = [];
      const newEntries: Array<{ entry: TournamentEntry; sourceFormatData: any }> = [];

      // 3. Copy all competitions
      for (const sourceCompetition of this.state.sourceCompetitions) {
        const newCompetition = tournamentCompetitionCopier.copy(sourceCompetition, {
          tournament_session_id: newSessionId,
          status: this.state.copyTournamentProgress ? sourceCompetition.status : "setup",
          format_data: this.state.copyTournamentProgress ? sourceCompetition.format_data : {},
        });
        await newCompetition.save();
        competitionIdMap.set(sourceCompetition.id!, newCompetition.id!);

        // Store for format_data update after entries are copied
        if (this.state.copyTournamentProgress) {
          newCompetitions.push({
            competition: newCompetition,
            sourceFormatData: sourceCompetition.format_data,
          });
        }
      }

      // 4. Copy all entries
      for (const sourceEntry of this.state.sourceEntries) {
        const newCompetitionId = competitionIdMap.get(sourceEntry.tournament_competition_id!);
        if (!newCompetitionId) {
          throw new Error(
            `Cannot find new competition ID for entry ${sourceEntry.id} with competition ${sourceEntry.tournament_competition_id}`,
          );
        }

        const newEntry = tournamentEntryCopier.copy(sourceEntry, {
          tournament_competition_id: newCompetitionId,
          format_data: this.state.copyTournamentProgress ? sourceEntry.format_data : {},
          eliminated: false,
          last_match_completed_at: undefined,
        });
        await newEntry.save();
        entryIdMap.set(sourceEntry.id!, newEntry.id!);

        // Store for format_data update after all entries are copied
        if (this.state.copyTournamentProgress) {
          newEntries.push({
            entry: newEntry,
            sourceFormatData: sourceEntry.format_data,
          });
        }
      }

      // 4b. Update format_data with mapped entry IDs (after all entries are copied)
      if (this.state.copyTournamentProgress) {
        // Update competition format_data
        for (const { competition, sourceFormatData } of newCompetitions) {
          if (sourceFormatData) {
            const updatedFormatData = replaceEntryIdsInFormatData(sourceFormatData, entryIdMap);
            competition.format_data = updatedFormatData;
            await competition.save();
          }
        }

        // Update entry format_data
        for (const { entry, sourceFormatData } of newEntries) {
          if (sourceFormatData) {
            const updatedFormatData = replaceEntryIdsInFormatData(sourceFormatData, entryIdMap);
            if (updatedFormatData && typeof updatedFormatData === "object" && "group" in updatedFormatData) {
              console.log("[GroupWrite][TournamentCopyController.copyTournament.entry.format_data]", {
                entryId: entry.id,
                competitionId: entry.tournament_competition_id,
                nextGroup: (updatedFormatData as Record<string, unknown>).group,
              });
            }
            entry.format_data = updatedFormatData;
            await entry.save();
          }
        }
      }

      // 5. Copy all registrations
      for (const sourceRegistration of this.state.sourceRegistrations) {
        const newPlayerId = playerIdMap.get(sourceRegistration.tournament_player_id!);
        const newCompetitionId = competitionIdMap.get(sourceRegistration.tournament_competition_id!);
        const newEntryId = entryIdMap.get(sourceRegistration.tournament_entry_id!);

        if (!newPlayerId || !newCompetitionId || !newEntryId) {
          // Skip registrations where we can't find the new IDs
          continue;
        }

        const newRegistration = tournamentRegistrationCopier.copy(sourceRegistration, {
          tournament_player_id: newPlayerId,
          tournament_competition_id: newCompetitionId,
          tournament_entry_id: newEntryId,
          partner_joined: false, // Reset partner status
          partner_email_sent_at: null,
          registration_received_email_sent_at: null,
          admin_signup_notification_sent_at: null,
          withdrawn_at: null,
        });
        // Explicitly clear excluded fields after creation to ensure they're saved
        newRegistration.partner_email_sent_at = undefined;
        newRegistration.registration_received_email_sent_at = undefined;
        newRegistration.admin_signup_notification_sent_at = undefined;
        newRegistration.withdrawn_at = undefined;
        await newRegistration.save();
      }

      // 6. Copy competition events if copying tournament progress
      if (this.state.copyTournamentProgress) {
        for (const sourceEvent of this.state.sourceEvents) {
          const newCompetitionId = competitionIdMap.get(sourceEvent.tournament_competition_id!);
          if (!newCompetitionId) {
            continue;
          }

          const mappedPayload = replaceEntryIdsInFormatData(sourceEvent.payload, entryIdMap);
          const newEvent = tournamentEventCopier.copy(sourceEvent, {
            tournament_competition_id: newCompetitionId,
            payload: mappedPayload,
          });
          await newEvent.save();
        }
      }
    });

    // Navigate to the new tournament
    if (!newSessionId) {
      throw new Error("Failed to create new tournament session");
    }
    routeService.navigate("/tournament/:id", {
      params: { id: newSessionId },
    });
  }
}

const CopyTournamentModal: React.FC<Props> = () => {
  const controller = CopyTournamentModalController.use();
  const routeController = RouteController.use();
  const { name, club_id, excludePersonalInfo, copyTournamentProgress, loading, clubs } = controller.state;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || loading) return;
    setIsSubmitting(true);
    try {
      await controller.actionCopyTournament();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppModal title="Copy tournament">
        <div className="flex items-center justify-center py-8">
          <p className="text-gray-600">Loading tournament data...</p>
        </div>
      </AppModal>
    );
  }

  return (
    <AppModal title="Copy tournament">
      <form onSubmit={handleSubmit}>
        <LabelledField label="Tournament name" htmlFor="tournament-name" required>
          <Input
            id="tournament-name"
            type="text"
            value={name}
            onChange={(e) => controller.setState({ name: e.target.value })}
            autoFocus
            disabled={isSubmitting}
          />
        </LabelledField>
        <LabelledField label="Club" htmlFor="club-id" required>
          <Select
            id="club-id"
            value={club_id}
            onChange={(e) => controller.setState({ club_id: e.target.value })}
            disabled={isSubmitting}
            required
          >
            {clubs.map((club) => (
              <option key={club.id} value={club.id}>
                {club.name}
              </option>
            ))}
          </Select>
        </LabelledField>
        <LabelledField
          label="Exclude personal information"
          htmlFor="exclude-personal-info"
          helpText="Remove email addresses and mobile numbers"
          inline
        >
          <Toggle
            id="exclude-personal-info"
            checked={excludePersonalInfo}
            onChange={(e) => controller.setState({ excludePersonalInfo: e.target.checked })}
            disabled={isSubmitting}
          />
        </LabelledField>
        <LabelledField
          label="Copy tournament progress"
          htmlFor="copy-tournament-progress"
          helpText="Copy all tournament progress including matches with results, player check-ins, competition status, and entry data. Useful for testing final stages."
          inline
        >
          <Toggle
            id="copy-tournament-progress"
            checked={copyTournamentProgress}
            onChange={(e) => controller.setState({ copyTournamentProgress: e.target.checked })}
            disabled={isSubmitting}
          />
        </LabelledField>
        <div className="flex justify-end gap-2 mt-6">
          <Button
            type="button"
            kind="secondary"
            onClick={() => routeController.actionCloseModal()}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || !name.trim() || !club_id}>
            {isSubmitting ? "Copying..." : "Copy"}
          </Button>
        </div>
      </form>
    </AppModal>
  );
};

export default injectModal(CopyTournamentModalController.scope(ApplicationView(CopyTournamentModal)));
